import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Clock, FloppyDisk } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TatConfigPage() {
  const [config, setConfig] = useState({
    processing_tat_hours: 24, dispatch_tat_hours: 48, cutoff_time: '18:00', cpt_hours: 72, auto_cancel_after_hours: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/tat-config`, { withCredentials: true });
      setConfig(data);
    } catch { /* defaults */ }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/seller/tat-config`, config, { withCredentials: true });
      toast.success('TAT configuration saved');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const Field = ({ label, desc, field, type = 'number', suffix }) => (
    <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
      <Label className="text-sm font-semibold text-[#0A0A0A]">{label}</Label>
      <p className="text-xs text-[#9c9db7] mb-3">{desc}</p>
      <div className="flex items-center gap-2">
        <Input data-testid={`tat-${field}`} type={type} value={config[field] ?? ''} onChange={e => setConfig(c => ({ ...c, [field]: type === 'number' ? (e.target.value ? parseInt(e.target.value) : null) : e.target.value }))} className="max-w-[200px]" />
        {suffix && <span className="text-sm text-[#6b6b8a]">{suffix}</span>}
      </div>
    </div>
  );

  if (loading) return <DashboardLayout><div className="p-6 text-center text-[#9c9db7]">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-3xl" data-testid="tat-config-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">TAT & Cutoff Configuration</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Set turnaround times, cutoff times, and customer promise times</p>
          </div>
          <Button data-testid="save-tat-btn" onClick={handleSave} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <FloppyDisk size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Config'}
          </Button>
        </div>

        <div className="grid gap-4">
          <div className="flex items-center gap-3 p-4 bg-[#7d64ff]/5 rounded-xl border border-[#7d64ff]/20">
            <Clock size={24} className="text-[#7d64ff]" />
            <div>
              <p className="text-sm font-semibold text-[#0A0A0A]">Order Timeline</p>
              <p className="text-xs text-[#6b6b8a]">Processing → Dispatch → Delivery = Customer Promise Time (CPT)</p>
            </div>
          </div>

          <Field label="Processing TAT" desc="Time allowed to process/pick an order after acceptance" field="processing_tat_hours" suffix="hours" />
          <Field label="Dispatch TAT" desc="Maximum time to dispatch after processing" field="dispatch_tat_hours" suffix="hours" />
          <Field label="Daily Cutoff Time" desc="Orders placed after this time are processed next business day" field="cutoff_time" type="time" />
          <Field label="Customer Promise Time (CPT)" desc="Total delivery promise from order placement" field="cpt_hours" suffix="hours" />
          <Field label="Auto-Cancel After" desc="Auto-cancel orders not processed within this time (leave empty to disable)" field="auto_cancel_after_hours" suffix="hours" />
        </div>
      </div>
    </DashboardLayout>
  );
}
